// LAP-752 — Grouped bar chart comparing the user's CO2 per category against
// the French ADEME average, with the active objective marked as a dashed
// line over each user bar. Highlights categories above the FR average in
// red so the user immediately sees where to act.
import { Box, Card } from '@mui/material'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import WTypography from '../../../../Components/whiteTypography'
import { wizardAPI } from '../../../../utils/wizardAPI'

const CATEGORY_LABEL_KEYS = {
  transport: ['dashboard.catTransport', 'Transport'],
  logement: ['dashboard.catLogement', 'Logement'],
  alimentation: ['dashboard.catAlimentation', 'Alimentation'],
  shopping: ['dashboard.catShopping', 'Shopping'],
}

function EcoVsFranceChart() {
  const { t } = useTranslation('layout')
  const [data, setData] = useState({ categories: [], fr_total_t: 9.2 })
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    wizardAPI
      .getEmissionVsFrance()
      .then((d) => {
        if (!cancelled) setData(d || { categories: [], fr_total_t: 9.2 })
      })
      .catch(() => {
        if (!cancelled) setData({ categories: [], fr_total_t: 9.2 })
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  // LAP-752 — Title adapts to the user's country (Belgium → "Vous vs. moyenne BE")
  // so non-French users don't see a "FR" label that doesn't apply to them.
  const countryLabel = data.country_name || 'FR'
  const countryDataOk = data.country_data_available !== false

  const W = 480
  const H = 230
  const padBottom = 30
  const padTop = 20
  const cats = data.categories || []

  // Y-axis scaling — max of user + FR across all categories.
  const maxY = Math.max(
    1,
    ...cats.flatMap((c) => [c.user_total_t || 0, c.fr_average_t || 0, c.objective_t || 0])
  )
  const yScale = (v) => H - padBottom - ((v || 0) / maxY) * (H - padTop - padBottom)
  const groupWidth = cats.length > 0 ? W / cats.length : 0
  const barWidth = 22

  // Detect "Shopping seul poste au-dessus" type messaging.
  const aboveCats = cats.filter((c) => c.status === 'above').map((c) => {
    const [k, fb] = CATEGORY_LABEL_KEYS[c.category] || []
    return k ? t(k, fb) : c.category
  })

  return (
    <Card className="dashboard_card__root1">
      <Box className="dashboard_card__header1" style={{ marginBottom: 8 }}>
        <WTypography component="span" variant="h6">
          {t('dashboard.vsCountryTitle', {
            country: countryLabel,
            defaultValue: `Vous vs. moyenne ${countryLabel}`,
          })}
        </WTypography>
        <WTypography component="span" sx={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>
          {t('dashboard.ademe2022', 'ADEME 2022')}
        </WTypography>
      </Box>

      {/* LAP-752 — Surface a fallback notice if the user's country has no
          NationalEmission data and we're showing France's baseline. Support
          team has already been emailed (24h cooldown) on the backend side. */}
      {!loading && !countryDataOk && data.country_name && (
        <Box
          sx={{
            mb: 1.5,
            px: 1.5,
            py: 1,
            borderRadius: 1,
            fontSize: 12,
            color: '#ffcdb8',
            bgcolor: 'rgba(217,119,87,0.12)',
            border: '1px solid rgba(217,119,87,0.3)',
          }}
        >
          {t('dashboard.countryFallbackNotice', {
            country: data.country_name,
            defaultValue:
              'Moyenne provisoire (France) pour {{country}} — données nationales en cours d\'ajout.',
          })}
        </Box>
      )}

      <Box display="flex" gap={2} mb={1} style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)' }}>
        <span>
          <span style={legendSwatch('#2fd28a')} /> {t('dashboard.legendYou', 'Vous')}
        </span>
        <span>
          <span style={legendSwatch('#9fb0d2', 0.5)} />{' '}
          {t('dashboard.legendCountryAvg', {
            country: countryLabel,
            defaultValue: `Moyenne ${countryLabel}`,
          })}
        </span>
        <span>
          <span style={legendSwatch('#7aa6ff')} /> {t('dashboard.legendObjective', 'Objectif')}
        </span>
      </Box>

      {loading ? (
        <Box height={230} display="flex" alignItems="center" justifyContent="center">
          <WTypography variant="body2" sx={{ color: 'rgba(255,255,255,0.4)' }}>
            {t('dashboard.loading', 'Chargement…')}
          </WTypography>
        </Box>
      ) : (
        <>
          <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 230, display: 'block' }}>
            {/* Gridlines */}
            {[0.25, 0.5, 0.75].map((r) => (
              <line
                key={r}
                x1={0}
                y1={padTop + r * (H - padTop - padBottom)}
                x2={W}
                y2={padTop + r * (H - padTop - padBottom)}
                stroke="rgba(255,255,255,0.06)"
              />
            ))}

            {cats.map((c, i) => {
              const baseX = i * groupWidth + (groupWidth / 2 - barWidth)
              const yFr = yScale(c.fr_average_t)
              const yUser = yScale(c.user_total_t)
              const userColor = c.status === 'above' ? '#ff5b6b' : '#2fd28a'
              const [k, fb] = CATEGORY_LABEL_KEYS[c.category] || []
              const label = k ? t(k, fb) : c.category
              return (
                <g key={c.category}>
                  {/* FR average bar */}
                  <rect
                    x={baseX}
                    y={yFr}
                    width={barWidth}
                    height={H - padBottom - yFr}
                    fill="rgba(159,176,210,0.35)"
                    rx="4"
                  />
                  {/* User bar */}
                  <rect
                    x={baseX + barWidth + 4}
                    y={yUser}
                    width={barWidth}
                    height={H - padBottom - yUser}
                    fill={userColor}
                    rx="4"
                  />
                  {/* Objective dashed marker (over user bar) */}
                  {c.objective_t != null && (
                    <line
                      x1={baseX + barWidth + 2}
                      y1={yScale(c.objective_t)}
                      x2={baseX + barWidth * 2 + 6}
                      y2={yScale(c.objective_t)}
                      stroke="#7aa6ff"
                      strokeWidth="2"
                      strokeDasharray="2 2"
                    />
                  )}
                  {/* Category label */}
                  <text
                    x={baseX + barWidth}
                    y={H - 10}
                    textAnchor="middle"
                    fill="rgba(255,255,255,0.55)"
                    fontSize="10"
                  >
                    {label}
                  </text>
                </g>
              )
            })}
          </svg>

          {aboveCats.length > 0 && (
            <WTypography
              variant="body2"
              sx={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginTop: 1 }}
            >
              🔴 {aboveCats.join(', ')} :{' '}
              {t('dashboard.aboveCountryNote', {
                country: countryLabel,
                defaultValue: `au-dessus de la moyenne ${countryLabel}`,
              })}
              .
            </WTypography>
          )}
        </>
      )}
    </Card>
  )
}

function legendSwatch(color, opacity = 1) {
  return {
    display: 'inline-block',
    width: 10,
    height: 10,
    borderRadius: 3,
    background: color,
    opacity,
    marginRight: 4,
    verticalAlign: 'middle',
  }
}

export default EcoVsFranceChart
